import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Plus, Mic, ArrowLeft, Clock, FileAudio, FileText } from "lucide-react";
import api, { getBoardroomToken, clearBoardroomToken } from "../utils/qccaApi";

const STATUS_LABEL = {
  draft: "Draft",
  audio_uploaded: "Audio uploaded",
  transcribed: "Transcribed",
  minutes_ready: "Minutes ready",
};

export default function QCCAClerkList() {
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getBoardroomToken()) { nav("/qcca/boardroom/login"); return; }
    api.get("/qcca/clerk/sessions")
      .then((r) => setSessions(r.data.sessions || []))
      .catch((e) => {
        if (e?.response?.status === 401) { clearBoardroomToken(); nav("/qcca/boardroom/login"); }
        else toast.error("Failed to load clerk sessions.");
      })
      .finally(() => setLoading(false));
  }, [nav]);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/qcca/boardroom" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 hover:text-[#C5A52A] mb-6">
          <ArrowLeft className="w-3 h-3" /> Boardroom
        </Link>

        <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Digital Clerk</div>
            <h1 className="mt-3 font-headline text-4xl text-white leading-tight">Your <span className="italic text-[#C5A52A]">meetings.</span></h1>
          </div>
          <Link to="/qcca/boardroom/clerk/new"
            className="inline-flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors"
            data-testid="qcca-clerk-new-btn">
            <Plus className="w-4 h-4" /> Record a new meeting
          </Link>
        </div>

        {loading ? (
          <div className="text-gray-500 font-mono text-sm">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="border border-dashed border-[#1a1a1a] bg-[#0a0a0a] p-12 text-center">
            <Mic className="w-10 h-10 text-[#C5A52A] mx-auto" />
            <h3 className="mt-4 font-headline text-2xl text-white">No meetings yet.</h3>
            <p className="mt-2 text-gray-400 text-sm max-w-md mx-auto">
              Start your next board meeting with the Digital Clerk — record in your browser or upload a file, and we'll handle the transcription and minutes.
            </p>
            <Link to="/qcca/boardroom/clerk/new" className="inline-flex items-center gap-2 mt-6 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors">
              <Plus className="w-4 h-4" /> New meeting
            </Link>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="qcca-clerk-list">
            {sessions.map((s) => (
              <Link key={s.id} to={`/qcca/boardroom/clerk/${s.id}`}
                className="block group bg-[#0a0a0a] border border-[#1a1a1a] hover:border-[#C5A52A]/60 p-6 transition-colors"
                data-testid={`qcca-clerk-row-${s.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-headline text-xl text-white leading-tight group-hover:text-[#C5A52A] transition-colors">
                      {s.title}
                    </h3>
                    <div className="mt-1 font-mono text-[11px] text-gray-500">
                      {s.meeting_date || new Date(s.created_at).toLocaleDateString()} · {(s.attendees || []).length} attendee{(s.attendees || []).length === 1 ? "" : "s"}
                    </div>
                    {s.transcript_preview && (
                      <p className="mt-3 text-gray-400 text-sm line-clamp-2 italic">"{s.transcript_preview}"</p>
                    )}
                  </div>
                  <div className="shrink-0 text-right space-y-2">
                    <span className={`inline-block border px-2 py-1 font-mono text-[9px] uppercase tracking-[0.22em] ${
                      s.status === "minutes_ready" ? "border-[#C5A52A]/60 text-[#C5A52A] bg-[#C5A52A]/5" : "border-[#1a1a1a] text-gray-400"
                    }`}>
                      {STATUS_LABEL[s.status] || s.status}
                    </span>
                    <div className="flex items-center gap-2 justify-end font-mono text-[10px] text-gray-500">
                      {s.audio_url && <FileAudio className="w-3 h-3 text-[#C5A52A]" />}
                      {s.minutes && <FileText className="w-3 h-3 text-[#C5A52A]" />}
                      <Clock className="w-3 h-3" />
                      <span>{new Date(s.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
